import type { Configuration, PopupRequest } from '@azure/msal-browser';

// Microsoft Entra ID (Azure AD) configuration for the LiveChat agent console (#1994).
//
// Values are baked in at build time from REACT_APP_ENTRA_* (per-env, mirroring the existing
// REACT_APP_OPENIDCONNECT_* customer-OIDC config — see OidcAuthHelper.ts). The
// LiveChatAgentEntraLoginEnabled feature flag stays OFF until both these and the backend `entra`
// provider config (appsettings) carry the real Entra app-registration coordinates.

const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID ?? '';
const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID ?? '';

// The API scope whose access token the backend Bearer_entra scheme accepts (its audience must
// match `Authentication:OpenIdConnectProviders:entra` in appsettings). Defaults to the app's own
// api://{clientId}/access_as_user when not explicitly overridden.
const apiScope = process.env.REACT_APP_ENTRA_API_SCOPE ?? (clientId ? `api://${clientId}/access_as_user` : '');

// True only when the build carries real Entra coordinates. AgentEntry uses this as a safety gate:
// if the flag is flipped on before the env vars are wired, we fall back to the Visma Online flow
// instead of constructing MSAL with an empty clientId (which would throw).
export const isEntraConfigured = (): boolean => Boolean(tenantId && clientId);

export const getEntraMsalConfig = (): Configuration => ({
    auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        // sessionStorage keeps the agent's Entra session in its own tab, decoupled from the
        // customer's Visma Online OIDC session (localStorage). That decoupling is the whole point
        // of #1994: the customer-side support/supportkod re-auth can no longer clobber it.
        cacheLocation: 'sessionStorage',
    },
});

// Requested at interactive sign-in. openid/profile/email yield the id-token claims the Cosmos
// agent allow-list matches on (preferred_username / upn / email); the api scope yields an access
// token the backend will accept. The empty-string filter drops the api scope while it is still a
// placeholder (so this object is never malformed even before configuration).
//
// prompt:'select_account' always shows the account chooser on interactive sign-in. This only fires
// when the agent actually hits the sign-in screen (no valid cached token, or after an explicit sign
// out) — silent token refresh never prompts — so it costs nothing on the common auto-resume path
// while letting an agent who signed in with the wrong account pick a different one. Without it, MSAL
// would silently reuse the browser's existing Entra SSO session and re-sign-in the same account,
// stranding a non-allow-listed agent on the not-authorized screen.
export const entraLoginRequest: PopupRequest = {
    scopes: ['openid', 'profile', 'email', apiScope].filter(Boolean),
    prompt: 'select_account',
};

// Used for silent (re)acquisition of the API access token sent to the backend.
export const entraTokenScopes: string[] = [apiScope].filter(Boolean);
